import { fetchConfig, debounce } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * Gift box (warehouse packaging).
 *
 * The warehouse needs one gift box line per product in the gift box collection (theme settings) on every
 * order. The gift box is a hidden, standalone cart line. Its quantity must always equal the total quantity
 * of the products that need a box.
 *
 * - On add to cart, the gift box is sent in the same `cart/add.js` request as the product.
 * - On quantity change, the new gift box quantity is sent in the same `cart/update.js` request as the line.
 * - After every cart change, and on load when the cart has items, the `gift-box-status` section is fetched
 *   at low priority. It carries the needed and current gift box quantity, computed in Liquid. When they
 *   differ, one silent `cart/update.js` corrects the cart. No re-render is needed: the gift box is hidden
 *   and excluded from the counts.
 */

const STATUS_SECTION_ID = 'gift-box-status';
const RECONCILE_DELAY = 1500;

/**
 * @typedef {object} GiftBoxConfig
 * @property {number} variantId - The gift box variant id.
 * @property {number} productId - The gift box product id.
 */

/**
 * Returns the gift box config, or null when no gift box product is selected in the theme settings.
 * @returns {GiftBoxConfig | null}
 */
export function getGiftBoxConfig() {
  // `Theme` is declared with `const` in a classic script (snippets/scripts.liquid), so it is a global
  // binding but not a property of `globalThis`. It must be referenced by name.
  if (typeof Theme === 'undefined') return null;
  const config = Theme.giftBox;
  if (!config || !config.variantId) return null;
  return config;
}

/**
 * Returns the gift box cart item to add alongside a product, or null when none is needed.
 * Whether a product needs a gift box is decided in Liquid (snippets/gift-box-required.liquid, membership of
 * the collection in the theme settings) and rendered as `data-gift-box-required` on the product form.
 * @param {string | undefined} giftBoxRequired - The `data-gift-box-required` value of the product form.
 * @param {number} quantity
 * @returns {{ id: number, quantity: number } | null}
 */
export function giftBoxItemFor(giftBoxRequired, quantity) {
  const config = getGiftBoxConfig();
  if (!config || giftBoxRequired !== 'true' || quantity <= 0) return null;
  return { id: config.variantId, quantity };
}

/**
 * Computes the gift box quantity the cart needs after a line quantity change, using the rendered rows.
 * @param {Element} root - A cart-items-component or any element containing the cart item rows.
 * @param {number} line - The 1-based cart line being changed.
 * @param {number} quantity - The new quantity for that line.
 * @returns {number}
 */
export function giftBoxQuantityAfterChange(root, line, quantity) {
  let needed = 0;
  root.querySelectorAll('[data-gift-box-required="true"]').forEach((row) => {
    if (!(row instanceof HTMLElement)) return;
    const rowLine = Number(row.dataset.line);
    needed += rowLine === line ? quantity : Number(row.dataset.quantity) || 0;
  });
  return needed;
}

/**
 * Adds the gift box quantity to a `cart/update.js` updates object, so the gift box follows the product
 * quantity in the same request. Setting a variant that is not in the cart to 0 is a no-op for Shopify.
 * @param {Record<string, number>} updates
 * @param {Element} root
 * @param {number} line
 * @param {number} quantity
 * @returns {Record<string, number>}
 */
export function withGiftBoxQuantity(updates, root, line, quantity) {
  const config = getGiftBoxConfig();
  if (!config) return updates;

  return { ...updates, [String(config.variantId)]: giftBoxQuantityAfterChange(root, line, quantity) };
}

/**
 * Fetches the gift box status section and returns the counts it carries.
 * @returns {Promise<{ needed: number, inCart: number } | null>}
 */
async function fetchGiftBoxStatus() {
  const root = window.Shopify?.routes?.root ?? '/';
  const url = `${root}?section_id=${STATUS_SECTION_ID}`;

  const response = await fetch(url, { priority: 'low', cache: 'no-store' });
  if (!response.ok) return null;

  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const status = html.querySelector('gift-box-status');
  if (!(status instanceof HTMLElement)) return null;

  return {
    needed: Number(status.dataset.needed) || 0,
    inCart: Number(status.dataset.quantity) || 0,
  };
}

/** @type {Promise<void> | null} */
let reconcileInFlight = null;
/** @type {string | null} */
let lastFailedSignature = null;

/**
 * Validates the cart and silently corrects the gift box quantity when it does not match.
 * Runs at most one check at a time, and does not retry the same failed correction.
 * @returns {Promise<void>}
 */
export function reconcileGiftBox() {
  const config = getGiftBoxConfig();
  if (!config) return Promise.resolve();
  if (reconcileInFlight) return reconcileInFlight;

  reconcileInFlight = fetchGiftBoxStatus()
    .then((counts) => {
      if (!counts || counts.needed === counts.inCart) return;

      const signature = `${counts.inCart}->${counts.needed}`;
      if (signature === lastFailedSignature) return;

      const body = JSON.stringify({ updates: { [String(config.variantId)]: counts.needed } });

      return fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
        .then((response) => response.json())
        .then((data) => {
          if (data.status || data.errors) {
            lastFailedSignature = signature;
            console.error('Gift box correction failed', data);
            return;
          }
          lastFailedSignature = null;
        });
    })
    .catch(console.error)
    .finally(() => {
      reconcileInFlight = null;
    });

  return reconcileInFlight;
}

const scheduleReconcile = debounce(() => {
  const run = () => reconcileGiftBox();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run);
  } else {
    setTimeout(run, 0);
  }
}, RECONCILE_DELAY);

/**
 * Whether the rendered page shows a cart with items.
 * @returns {boolean}
 */
function cartHasItems() {
  const count = document.querySelector('[ref="cartItemCount"]')?.textContent;
  return Number(count) > 0;
}

if (getGiftBoxConfig()) {
  document.addEventListener(ThemeEvents.cartUpdate, scheduleReconcile);

  if (document.readyState === 'complete') {
    if (cartHasItems()) scheduleReconcile();
  } else {
    window.addEventListener('load', () => cartHasItems() && scheduleReconcile(), { once: true });
  }
}
